import { useState, useEffect } from "react";
import { HabitStorage, type CustomHabit, type TrainingKind } from "./habitTypes";

interface HabitManagerProps {
  onStartTraining: (habitId: string, kind: TrainingKind) => void;
  onStartDetection: () => void;
}

export function HabitManager({ onStartTraining, onStartDetection }: HabitManagerProps) {
  const [habits, setHabits] = useState<CustomHabit[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newHabitName, setNewHabitName] = useState("");

  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = () => {
    const loaded = HabitStorage.loadHabits();
    setHabits(loaded);
  };

  const handleCreateHabit = () => {
    if (!newHabitName.trim()) return;
    HabitStorage.addHabit(newHabitName.trim());
    setNewHabitName("");
    setIsCreating(false);
    loadHabits();
  };

  const handleDeleteHabit = (habitId: string) => {
    if (confirm("Are you sure you want to delete this habit?")) {
      HabitStorage.deleteHabit(habitId);
      loadHabits();
    }
  };

  const handleToggleHabit = (habitId: string) => {
    HabitStorage.toggleHabit(habitId);
    loadHabits();
  };

  const handleClearTraining = (habitId: string, kind: TrainingKind | "all") => {
    const message =
      kind === "all"
        ? "Clear all training data for this habit?"
        : kind === "neutral"
        ? "Clear neutral baseline samples for this habit?"
        : "Clear habit action samples for this habit?";
    if (confirm(message)) {
      HabitStorage.clearTrainingSamples(habitId, kind === "all" ? "all" : kind);
      loadHabits();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Custom Habits</h2>
          <p className="text-white/60 text-sm mt-1">
            Create and train the app to detect your specific habits
          </p>
        </div>
        <button
          onClick={onStartDetection}
          className="px-6 py-3 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-xl transition shadow-lg shadow-sky-500/20"
        >
          Start Detection
        </button>
      </div>

      {/* Create new habit */}
      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
        {!isCreating ? (
          <button
            onClick={() => setIsCreating(true)}
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-white/20 hover:border-white/40 rounded-xl text-white/70 hover:text-white transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="font-semibold">Create New Habit</span>
          </button>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              value={newHabitName}
              onChange={(e) => setNewHabitName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateHabit();
                if (e.key === "Escape") {
                  setIsCreating(false);
                  setNewHabitName("");
                }
              }}
              placeholder="Enter habit name (e.g., 'Hair twirling', 'Pen clicking')"
              className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-sky-400/60"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateHabit}
                disabled={!newHabitName.trim()}
                className="flex-1 px-4 py-2 bg-sky-500 hover:bg-sky-400 disabled:bg-white/10 disabled:text-white/40 text-white font-semibold rounded-lg transition"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewHabitName("");
                }}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Habits list */}
      {habits.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-lg">No custom habits yet</p>
          <p className="text-sm mt-1">Create your first habit to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {habits.map((habit) => (
            <div
              key={habit.id}
              className={`rounded-2xl border ${
                habit.enabled ? "border-sky-400/30 bg-sky-400/5" : "border-white/10 bg-white/5"
              } backdrop-blur p-5 transition`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-white truncate">{habit.name}</h3>
                    {habit.enabled && (
                      <span className="flex-shrink-0 px-2 py-0.5 bg-green-400/20 border border-green-400/40 rounded-full text-xs font-semibold text-green-300 uppercase tracking-wider">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-white/60">
                    <span>
                      {habit.habitSamples.length} habit sample{habit.habitSamples.length !== 1 ? "s" : ""}
                    </span>
                    <span>•</span>
                    <span>
                      {habit.neutralSamples.length} neutral sample{habit.neutralSamples.length !== 1 ? "s" : ""}
                    </span>
                    <span>•</span>
                    <span>Created {new Date(habit.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Toggle switch */}
                <button
                  onClick={() => handleToggleHabit(habit.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${
                    habit.enabled ? "bg-sky-500" : "bg-white/20"
                  }`}
                  title={habit.enabled ? "Disable habit" : "Enable habit"}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      habit.enabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => onStartTraining(habit.id, "neutral")}
                  className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-200 font-semibold rounded-lg transition text-sm"
                >
                  {habit.neutralSamples.length === 0 ? "Record Baseline" : "Add Baseline"}
                </button>
                <button
                  onClick={() => onStartTraining(habit.id, "habit")}
                  className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-200 font-semibold rounded-lg transition text-sm"
                >
                  {habit.habitSamples.length === 0 ? "Record Habit" : "Add Habit Samples"}
                </button>
                {(habit.habitSamples.length > 0 || habit.neutralSamples.length > 0) && (
                  <div className="flex items-center gap-2 ml-auto">
                    {habit.habitSamples.length > 0 && (
                      <button
                        onClick={() => handleClearTraining(habit.id, "habit")}
                        className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white font-semibold rounded-lg transition text-xs"
                      >
                        Clear Habit
                      </button>
                    )}
                    {habit.neutralSamples.length > 0 && (
                      <button
                        onClick={() => handleClearTraining(habit.id, "neutral")}
                        className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white font-semibold rounded-lg transition text-xs"
                      >
                        Clear Baseline
                      </button>
                    )}
                    <button
                      onClick={() => handleClearTraining(habit.id, "all")}
                      className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 text-red-300 font-semibold rounded-lg transition text-xs"
                    >
                      Clear All
                    </button>
                  </div>
                )}
                <button
                  onClick={() => handleDeleteHabit(habit.id)}
                  className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 text-red-300 font-semibold rounded-lg transition text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info section */}
      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5">
        <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-3">How Training Works</h3>
        <ol className="space-y-2 text-sm text-white/70">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-semibold text-xs">1</span>
            <span>Create a custom habit with a descriptive name</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-semibold text-xs">2</span>
            <span>Record a <strong className="text-white">baseline</strong> clip with hands away from your face</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-semibold text-xs">3</span>
            <span>Record the <strong className="text-white">habit action</strong> (e.g., nail biting) near your face</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-semibold text-xs">4</span>
            <span>Add more samples as needed, then enable the habit for detection</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
